import React, { useEffect, useState } from "react";
import { Row, Col, Button, Container, Badge, Spinner } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getOnlineBookingSettings } from "../../acommsapi";
import { getUserPracticeById } from "../../api";
import AddEditOnlineBookings from "./AddEditOnlineBookings";

export function ViewOnlineBookings({ refresh, onOnlineBookingUpdate = () => {} }) {
  const [modalShow, setModalShow] = useState(false);
  const [practices, setPractices] = useState([]);
  const [onlineBookingData, setOnlineBookingData] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadBookingData = async () => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      const practiceNumbers = decoded.practice || [];

      const userPractices = await getUserPracticeById(decoded._id);
      setPractices(userPractices);

      const result = await getOnlineBookingSettings(practiceNumbers);
      setOnlineBookingData(Array.isArray(result.data) ? result.data : []);
      setLogoError(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookingData();
  }, [refresh]);

  const handleButtonClick = () => {
    setSelectedBooking(onlineBookingData.length > 0 ? onlineBookingData[0] : null);
    setModalShow(true);
  };

  const mapToNames = (numbersArray) => {
    if (!Array.isArray(numbersArray)) return [];
    return numbersArray
      .map((num) => {
        const p = practices.find((pr) => pr.practiceNumber === num);
        return p ? p.practiceName : num;
      })
      .filter(Boolean);
  };

  const renderLogo = () => {
    const logoUrl = onlineBookingData[0]?.logo?.[0] || null;

    if (logoUrl && !logoError) {
      return (
        <img
          src={logoUrl}
          alt="Practice Logo"
          style={{ maxHeight: "60px", maxWidth: "200px", objectFit: "contain" }}
          onError={() => setLogoError(true)}
        />
      );
    }
    return <span className="text-muted">No logo configured</span>;
  };

  const booking = onlineBookingData[0];

  return (
    <Container fluid className="pt-2 acc-list">
      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <h5 className="text-primary mb-2">Online Booking Configuration</h5>
        <Button onClick={handleButtonClick} variant="primary">
          {onlineBookingData.length > 0
            ? "Edit Online Booking Settings"
            : "Add Online Booking Settings"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading online booking settings…</p>
        </div>
      ) : !booking ? (
        <p className="text-center">
          No online booking settings configured yet. Use the button above to set
          them up.
        </p>
      ) : (
        <div className="mb-3">
          <Row className="mb-3">
            <Col md={6}>
              <div className="fw-semibold">Booking Page Heading</div>
              <div className="mt-1">{booking.bookingHeading || "Not set"}</div>
            </Col>
            <Col md={6}>
              <div className="fw-semibold">Logo</div>
              <div className="mt-1">{renderLogo()}</div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <div className="fw-semibold">Practices Enabled</div>
              <div className="mt-1">
                {mapToNames(booking.selectedPractices || []).map((name, idx) => (
                  <Badge key={idx} bg="secondary" className="me-2 mb-1">
                    {name}
                  </Badge>
                ))}
                {(!booking.selectedPractices ||
                  booking.selectedPractices.length === 0) && (
                  <span className="text-muted">No practices selected</span>
                )}
              </div>
            </Col>
          </Row>

          {booking.locations?.length > 0 && (
            <Row className="mb-3">
              <Col>
                <div className="fw-semibold">Locations</div>
                <ul className="mb-0 mt-1">
                  {booking.locations.map((loc, i) => (
                    <li key={i}>
                      <span className="fw-semibold">{loc.heading}</span>: {loc.address}
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={3}>
              <div className="fw-semibold">Allow Over Padding</div>
              <div className="mt-1">
                <Badge bg={booking.allowOverPadding ? "success" : "secondary"}>
                  {booking.allowOverPadding ? "Yes" : "No"}
                </Badge>
              </div>
            </Col>
            <Col md={3}>
              <div className="fw-semibold">Allow Overlap</div>
              <div className="mt-1">
                <Badge bg={booking.allowOverlap ? "success" : "secondary"}>
                  {booking.allowOverlap ? "Yes" : "No"}
                </Badge>
              </div>
            </Col>
            <Col md={3}>
              <div className="fw-semibold">Overlap Minutes</div>
              <div className="mt-1">
                {booking.allowOverlap && booking.overlapMinutes
                  ? booking.overlapMinutes
                  : "N/A"}
              </div>
            </Col>
            <Col md={3}>
              <div className="fw-semibold">Lead Minutes</div>
              <div className="mt-1">
                {booking.leadingMinutes ? booking.leadingMinutes : "N/A"}
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <div className="fw-semibold">Service Catalog Types</div>
              <div className="mt-1">
                {booking.serviceCatalog?.length > 0 ? (
                  booking.serviceCatalog
                    .filter((s) => s && s.trim())
                    .map((s, idx) => (
                      <Badge key={idx} bg="light" text="dark" className="me-2 mb-1 border">
                        {s}
                      </Badge>
                    ))
                ) : (
                  <span className="text-muted">No service types configured</span>
                )}
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <div className="fw-semibold">Service Notes</div>
              <div className="mt-1">
                {booking.serviceNotes ? booking.serviceNotes : "N/A"}
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <div className="fw-semibold">Practice Notes</div>
              <div className="mt-1">
                {booking.practiceNotes ? booking.practiceNotes : "N/A"}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="fw-semibold">Booking URL</div>
              <div className="mt-1">
                <p className="mb-0">{`https://practoplatform.com/#/onlinebooking/${booking._id}`}</p>
              </div>
            </Col>
          </Row>
        </div>
      )}

      <AddEditOnlineBookings
        show={modalShow}
        onHide={() => setModalShow(false)}
        onlineBookingDetail={selectedBooking}
        onOnlineBookingUpdate={() => {
          setModalShow(false);
          loadBookingData();
          onOnlineBookingUpdate();
        }}
      />
    </Container>
  );
}
